// V2Modal.tsx — SEGMENT 1/4
import { scryImageUrl } from "@/lib/scryClient";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { X, Heart, Plus, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddToCollectionModal from "@/components/AddToCollectionModal";
import { auth, db } from "@/lib/firebase";
import {
  getDocs,
  query,
  where,
  addDoc,
  collection,
  serverTimestamp,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { toast } from "@/components/ui/use-toast";
import { useHighlightCollectionNames } from "@/hooks/useHighlightCollectionNames";
import { useNavigate } from "react-router-dom";
import { PriceOverlay } from "@/components/PriceOverlay";
import api from "@/lib/scryApiClient";

// --- Types ---
type ScryfallCard = any;
type ScryfallRuling = {
  source: string;
  published_at: string;
  comment: string;
};

type V2ModalProps = {
  open: boolean;
  baseCard: { scryfall_id?: string | null; oracle_id?: string | null; name?: string | null } | null;
  onClose: () => void;
  onAdded?: () => void;
  closeOnAdd?: boolean;
  inTradesPage?: boolean;
  preload?: {
    card?: ScryfallCard | null;
    prints?: ScryfallCard[] | null;
    variants?: ScryfallCard[] | null; // ← accept variants from caller
    rulings?: ScryfallRuling[] | null;
  } | null;
  onAddToCollection?: (card: ScryfallCard) => void;
  onAddToWishlist?: (card: ScryfallCard) => void;
  onCopyImage?: (card: ScryfallCard) => void; // optional: reuse existing copy handler if you have it
}

/*
  Backend-only data layer
  - All data comes from /api/scry
  - No scry.* calls
  - UI/UX unchanged
*/

// --- Constants / helpers ---
const SPELLBOOK_BASE = "https://commanderspellbook.com";

function getCardImageUrl(card: any, size: "small" | "normal" | "large" = "large"): string {
  if (!card) return "/images/sg-card-back.png";
  const face = card.card_faces?.[0]?.image_uris;
  const fromObj =
    (size === "large" ? (face?.large || card.image_uris?.large) : null) ||
    (size === "normal" ? (face?.normal || card.image_uris?.normal) : null) ||
    (size === "small" ? (face?.small || card.image_uris?.small) : null) ||
    face?.large ||
    card.image_uris?.large ||
    face?.normal ||
    card.image_uris?.normal ||
    face?.small ||
    card.image_uris?.small ||
    null;

  if (fromObj) return fromObj;
  return card?.id ? scryImageUrl(card.id, size) : "/images/sg-card-back.png";
}

async function copyImageBlob(url: string): Promise<"image" | "url" | "fail"> {
  if (!url) return "fail";
  try {
    const res = await fetch(url, { mode: "cors", cache: "force-cache" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    // @ts-ignore
    if (navigator.clipboard && navigator.clipboard.write && window.ClipboardItem) {
      // @ts-ignore
      const item = new ClipboardItem({ [blob.type]: blob });
      // @ts-ignore
      await navigator.clipboard.write([item]);
      return "image";
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return "url";
    }
  } catch {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return "url";
      }
    } catch {}
  }
  return "fail";
}
async function handleCopyImage(card: ScryfallCard | null) {
  if (!card) return;
  const url = getCardImageUrl(card);
  const mode = await copyImageBlob(url);
  if (mode === "image") toast({ description: "Image copied to clipboard ✓" });
  else if (mode === "url") toast({ description: "Image URL copied ✓" });
  else {
    try {
      await navigator.clipboard?.writeText(url);
      toast({ description: "Image URL copied ✓" });
    } catch {
      toast({ description: "Could not copy image." });
    }
  }
}

// Wizards rules quick links
function buildReleaseNotesSearch(setName?: string | null, cardName?: string | null) {
  const q = ["site:magic.wizards.com", '"Release Notes"', setName || "", cardName ? `"${cardName}"` : ""]
    .filter(Boolean)
    .join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}
function buildComprehensiveRulesSearch(cardName?: string | null) {
  const q = ["site:media.wizards.com", '"Comprehensive Rules"', cardName ? `"${cardName}"` : ""]
    .filter(Boolean)
    .join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

// Normalize curly quotes, odd spaces, and collapse whitespace
const SPB_QUOTE_FIX = /[’‘`´]/g;
const SPB_NBSP_FIX = /[\u00A0\u2007\u202F]/g;
function normalizeSpellbookName(name?: string | null) {
  return (name || "")
    .replace(SPB_QUOTE_FIX, "'")
    .replace(SPB_NBSP_FIX, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function commanderSpellbookSearchUrl(cardName: string) {
  const n = normalizeSpellbookName(cardName);
  return `${SPELLBOOK_BASE}/search/?q=${encodeURIComponent(`card="${n}"`)}`;
}

function spellbookEmbedSrc(cardName: string) {
  const n = normalizeSpellbookName(cardName);
  return `${SPELLBOOK_BASE}/search/?q=${encodeURIComponent(`card="${n}"`)}`;
}
function nameAliasesForCard(card: ScryfallCard | null): string[] {
  if (!card?.name) return [];
  const pieces = card.name.split("//").map(s => s.trim()).filter(Boolean);
  return Array.from(new Set([card.name, ...pieces]));
}

// Sort newest → oldest, then set then collector_number
function sortPrintsNewestFirst(list: ScryfallCard[]): ScryfallCard[] {
  return [...list].sort((a, b) => {
    const da = a.released_at ? Date.parse(a.released_at) : 0;
    const db = b.released_at ? Date.parse(b.released_at) : 0;
    if (db !== da) return db - da;
    const sa = a.set || "";
    const sb = b.set || "";
    if (sb !== sa) return sb.localeCompare(sa);
    const ca = parseInt(a.collector_number || "0", 10) || 0;
    const cb = parseInt(b.collector_number || "0", 10) || 0;
    return cb - ca;
  });
}

// ADD: dedupe by card id
function uniqById<T extends { id?: string | null }>(arr: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const x of arr || []) {
    const k = (x?.id ?? "") as string;
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(x);
  }
  return out;
}

// ADD: safe set code helper used in debug logging
function setCodeOf(c: ScryfallCard | null): string {
  return c?.set ? String(c.set).toUpperCase() : "";
}

// Extract og:image from a combo page (for image-only embed)
function extractOgImageFromHtml(html: string): string | null {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const og =
      doc.querySelector('meta[property="og:image"]')?.getAttribute("content") ||
      doc.querySelector('meta[name="twitter:image"]')?.getAttribute("content");
    if (!og) return null;
    return new URL(og, SPELLBOOK_BASE).toString();
  } catch {
    return null;
  }
}

// Sizing / mode
const CARD_WIDTH = 306; // 360*0.85
const CARD_GAP = 27; // 32*0.85
const MODE_PREF_KEY = "v2ModalMode";
const MOBILE_BREAKPOINT = 768;

// ---------------- Backend client (only /api/scry) ----------------
function isUuid(v?: string | null) {
  return !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
}

// ADD: direct backend cards endpoint so we bypass any Scryfall/Firestore fallbacks
const RAW_CARDS_URL: string = (import.meta as any)?.env?.VITE_SCRY_CARDS_URL || "/api/scry/cards";
const CARDS_SEARCH_URL = /\/search\/?$/.test(RAW_CARDS_URL)
  ? RAW_CARDS_URL
  : `${RAW_CARDS_URL.replace(/\/+$/, "")}/search`;

async function backendCardsSearch(q: string, limit = 200, signal?: AbortSignal) {
  const url = new URL(CARDS_SEARCH_URL, window.location.origin);
  url.searchParams.set("q", q);
  url.searchParams.set("limit", String(limit));
  // Force prints fanout for variants
  if (/oracleid:/i.test(q)) url.searchParams.set("unique", "prints");
  // Prefer newest-first when supported
  url.searchParams.set("order", "released");
  url.searchParams.set("dir", "desc");
  if ((import.meta as any)?.env?.DEV) console.debug("[V2Modal] GET", url.toString());
  const res = await fetch(url.toString(), { signal, headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ADD: backend search with extra params (for name-based prints with unique=prints)
async function backendCardsSearchWith(
  q: string,
  limit = 200,
  extra: Record<string, string> = {},
  signal?: AbortSignal
) {
  const url = new URL(CARDS_SEARCH_URL, window.location.origin);
  url.searchParams.set("q", q);
  url.searchParams.set("limit", String(limit));
  for (const [k, v] of Object.entries(extra)) {
    if (v != null) url.searchParams.set(k, String(v));
  }
  if ((import.meta as any)?.env?.DEV) console.debug("[V2Modal] GET", url.toString());
  const res = await fetch(url.toString(), { signal, headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ADD: escape quotes for exact-name queries
function escName(name: string) {
  return String(name || "").replace(/"/g, '\\"').trim();
}

// ADD: backend prints by exact name (unique=prints, newest->oldest)
async function backendPrintsByName(name: string, signal?: AbortSignal) {
  const q = `name:"${escName(name)}"`;
  const payload = await backendCardsSearchWith(
    q,
    200,
    { unique: "prints", order: "released", dir: "desc" },
    signal
  );
  return toCardArray(payload);
}

async function backendPrintsByOracle(oracleId: string, signal?: AbortSignal) {
  const payload = await backendCardsSearch(`oracleid:${oracleId}`, 200, signal);
  return toCardArray(payload);
}

// Fallback-aware prints fetch: backend first, then client api.cardsSearch
async function printsByOracle(oracleId: string, signal?: AbortSignal): Promise<ScryfallCard[]> {
  try {
    const arr = await backendPrintsByOracle(oracleId, signal);
    if (arr.length) return arr;
  } catch {}
  try {
    if (typeof (api as any)?.cardsSearch === "function") {
      const lst: any = await (api as any).cardsSearch(
        { q: `oracleid:${oracleId}`, unique: "prints", limit: 200, order: "released", dir: "desc" } as any,
        signal
      );
      const arr = toCardArray(lst);
      if (arr.length) return arr;
    }
  } catch {}
  return [];
}

// ADD: Fallback-aware prints by name: backend first, then client api.cardsSearch
async function printsByName(name: string, signal?: AbortSignal): Promise<ScryfallCard[]> {
  try {
    const arr = await backendPrintsByName(name, signal);
    if (arr.length) return arr;
  } catch {}
  try {
    if (typeof (api as any)?.cardsSearch === "function") {
      const lst: any = await (api as any).cardsSearch(
        { q: `name:"${name}"`, unique: "prints", limit: 200, order: "released", dir: "desc" } as any,
        signal
      );
      const arr = toCardArray(lst);
      if (arr.length) return arr;
    }
  } catch {}
  return [];
}

// ADD: bundle resolver using backend endpoints + api fallbacks
async function fetchBundleFromBackend(
  keys: { scryfall_id?: string | null; oracle_id?: string | null; name?: string | null },
  signal?: AbortSignal
) {
  let card: ScryfallCard | null = null;
  let prints: ScryfallCard[] = [];
  let rulings: ScryfallRuling[] | null = null;

  // 1) by scryfall id
  if (keys.scryfall_id) {
    try { card = await backendCardById(keys.scryfall_id, signal); } catch {}
  }

  // 2) by name (prefer full prints fanout)
  if (!card && keys.name) {
    try {
      prints = await printsByName(keys.name, signal);
      card = prints[0] || null;
    } catch {}
  }

  // 3) by oracle id (fallback path)
  if (!card && keys.oracle_id) {
    try {
      prints = await printsByOracle(keys.oracle_id, signal);
      card = prints[0] || null;
    } catch {}
  }

  // 4) exact name fallback to single card if still unresolved
  if (!card && keys.name) {
    try {
      if (typeof (api as any)?.getCardByNamedExact === "function") {
        card = await (api as any).getCardByNamedExact(keys.name, signal);
      } else {
        const payload = await backendCardsSearch(keys.name, 1, signal);
        card = toCardArray(payload)[0] || null;
      }
    } catch {}
  }

  // 5) ensure prints for resolved card: try name first, then oracle
  if (card && prints.length === 0) {
    try {
      const byName = await printsByName(card.name, signal);
      prints = byName.length ? byName : prints;
    } catch {}
  }
  if (card?.oracle_id && prints.length === 0) {
    try { prints = await printsByOracle(card.oracle_id, signal); } catch {}
  }

  // 6) rulings
  if (card?.id) {
    try {
      if (typeof (api as any)?.rulingsById === "function") {
        const r = await (api as any).rulingsById(card.id, signal);
        rulings = Array.isArray(r?.data) ? (r.data as ScryfallRuling[]) : Array.isArray(r) ? (r as ScryfallRuling[]) : null;
      }
      if (!rulings) {
        rulings = await backendRulingsById(card.id, signal);
      }
    } catch {
      try { rulings = await backendRulingsById(card.id, signal); } catch {}
    }
  }

  const ordered = uniqById(sortPrintsNewestFirst(prints.length ? prints : card ? [card] : []));
  return { card, prints: ordered, rulings };
}

// ADD: enrich variants for a single resolved card (prefer name)
async function enrichVariantsFromBackend(resolved: ScryfallCard, signal?: AbortSignal): Promise<ScryfallCard[]> {
  if (!resolved?.name) return resolved?.oracle_id ? uniqById(await printsByOracle(resolved.oracle_id, signal)) : uniqById([resolved]);
  const byName = await printsByName(resolved.name, signal);
  if (byName.length) return uniqById(sortPrintsNewestFirst(byName));
  if (resolved?.oracle_id) {
    const byOid = await printsByOracle(resolved.oracle_id, signal);
    if (byOid.length) return uniqById(sortPrintsNewestFirst(byOid));
  }
  return uniqById([resolved]);
}

// Add near the top (after helpers)
const RULINGS_CACHE = new Map<string, ScryfallRuling[]>();

function parseRulings(payload: any): ScryfallRuling[] {
  try { if (typeof payload === "string") payload = JSON.parse(payload); } catch {}
  if (Array.isArray(payload)) return payload as ScryfallRuling[];
  if (Array.isArray(payload?.data)) return payload.data as ScryfallRuling[];
  if (payload?.object === "list" && Array.isArray(payload?.data)) return payload.data as ScryfallRuling[];
  return [];
}

async function fetchRulingsResilient(
  opts: { id?: string | null; oracle_id?: string | null },
  signal?: AbortSignal
): Promise<ScryfallRuling[]> {
  const { id, oracle_id } = opts;

  // Cache by concrete keys
  const ckId = id ? `id:${id}` : "";
  const ckOid = oracle_id ? `oid:${oracle_id}` : "";
  if (ckId && RULINGS_CACHE.has(ckId)) return RULINGS_CACHE.get(ckId)!;
  if (!ckId && ckOid && RULINGS_CACHE.has(ckOid)) return RULINGS_CACHE.get(ckOid)!;

  // 1) Try client API (dev proxy)
  try {
    if (id && typeof (api as any)?.rulingsById === "function") {
      const r = await (api as any).rulingsById(id, signal);
      const arr = parseRulings(r);
      if (arr.length) {
        RULINGS_CACHE.set(ckId, arr);
        return arr;
      }
    }
  } catch {}

  // 2) Hosting REST (prod rewrite)
  try {
    if (id) {
      const r = await fetch(`/api/scry/cards/${encodeURIComponent(id)}/rulings`, { signal, headers: { Accept: "application/json" } });
      if (r.ok) {
        const js = await r.json().catch(() => null);
        const arr = parseRulings(js);
        if (arr.length) {
          RULINGS_CACHE.set(ckId, arr);
          return arr;
        }
      }
    }
  } catch {}
  try {
    if (oracle_id) {
      const r = await fetch(`/api/scry/cards/oracle/${encodeURIComponent(oracle_id)}/rulings`, { signal, headers: { Accept: "application/json" } });
      if (r.ok) {
        const js = await r.json().catch(() => null);
        const arr = parseRulings(js);
        if (arr.length) {
          RULINGS_CACHE.set(ckOid, arr);
          return arr;
        }
      }
    }
  } catch {}

  // 3) Public Scryfall
  try {
    if (id) {
      const r = await fetch(`https://api.scryfall.com/cards/${encodeURIComponent(id)}/rulings`, { signal });
      if (r.ok) {
        const js = await r.json().catch(() => null);
        const arr = parseRulings(js);
        if (arr.length) {
          RULINGS_CACHE.set(ckId, arr);
          return arr;
        }
      }
    }
  } catch {}
  try {
    if (oracle_id) {
      const r = await fetch(`https://api.scryfall.com/cards/oracle/${encodeURIComponent(oracle_id)}/rulings`, { signal });
      if (r.ok) {
        const js = await r.json().catch(() => null);
        const arr = parseRulings(js);
        if (arr.length) {
          RULINGS_CACHE.set(ckOid, arr);
          return arr;
        }
      }
    }
  } catch {}

  return [];
}

// ---------------- Component ----------------
export function V2Modal(props: V2ModalProps) {
  const {
    open,
    baseCard,
    onClose,
    onAdded,
    closeOnAdd = true,
    inTradesPage = false,
    preload,
    onAddToCollection,
    onAddToWishlist,
    onCopyImage,
  } = props;

  const navigate = useNavigate();

  // state
  const [base, setBase] = useState<ScryfallCard | null>(null);
  const [variants, setVariants] = useState<ScryfallCard[]>([]);
  const [rulings, setRulings] = useState<ScryfallRuling[] | null>(null);
  // Track which variant is displayed (default to base when modal opens)
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Track current face index for double-faced cards by Scryfall id
  const [faceIndexById, setFaceIndexById] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Track if we actually hydrated variants from preload
  const hydratedFromPreloadRef = useRef(false);

  // dev-only: silence duplicate logs under StrictMode
  const didLogRef = useRef(false);
  useEffect(() => {
    if (!open || didLogRef.current) return;
    didLogRef.current = true;
    if (debugOn) {
      console.log("[V2Modal] open with baseCard:", baseCard);
      console.log("[V2Modal] preload:", preload);
    }
    return () => { didLogRef.current = false; };
  }, [open]);

  // Zoom
  const [zoomCard, setZoomCard] = useState<ScryfallCard | null>(null);

  // Add flow
  const [addCard, setAddCard] = useState<ScryfallCard | null>(null);
  const [addRegularPrice, setAddRegularPrice] = useState<number | null>(null);
  const [addFoilPrice, setAddFoilPrice] = useState<number | null>(null);

  // Wishlist
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlistDocId, setWishlistDocId] = useState<string | null>(null);
  const [wishlistDisabled, setWishlistDisabled] = useState(false);

  // Right pane tab
  const [rightTab, setRightTab] = useState<"details" | "rulings">("details");

  // tiny debug switch
  const [debugOn] = useState(() => localStorage.getItem("v2modal:debug") === "1");

  // Modes
  const [focusedMode, setFocusedMode] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(MODE_PREF_KEY);
      if (stored === "focus") return true;
      if (stored === "grid") return false;
    } catch {}
    if (typeof window !== "undefined") return window.innerWidth < MOBILE_BREAKPOINT;
    return true;
  });
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const stripRef = useRef<HTMLDivElement | null>(null);

  // Filmstrip navigation
  const prevVariant = useCallback(() => {
    setFocusedIndex(i => Math.max(0, i - 1));
  }, []);
  const nextVariant = useCallback(() => {
    setFocusedIndex(i => Math.min((variants.length ? variants.length - 1 : 0), i + 1));
  }, [variants.length]);

  function toggleMode() {
    setFocusedMode(prev => {
      const next = !prev;
      try {
        localStorage.setItem(MODE_PREF_KEY, next ? "focus" : "grid");
      } catch {}
      if (next) requestAnimationFrame(() => scrollToCard(focusedIndex, "auto"));
      else {
        requestAnimationFrame(() => {
          document.querySelector<HTMLElement>("[data-current-focused-card]")?.scrollIntoView({
            block: "center",
            inline: "center",
            behavior: "smooth",
          });
        });
      }
      return next;
    });
    setZoomCard(null);
  }

  const [sidePad, setSidePad] = useState(0);
  useEffect(() => {
    const calc = () => setSidePad(Math.max(0, (window.innerWidth - CARD_WIDTH) / 2));
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (focusedMode) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, focusedMode]);

  const scrollToCard = useCallback(
    (idx: number, behavior: ScrollBehavior = "smooth") => {
      if (!stripRef.current) return;
      const el = stripRef.current;
      const count = variants.length;
      if (!count) return;
      const clamped = Math.max(0, Math.min(idx, count - 1));
      const target = clamped * (CARD_WIDTH + CARD_GAP);
      el.scrollTo({ left: target, behavior });
    },
    [variants.length]
  );

  useEffect(() => {
    if (!focusedMode) return;
    scrollToCard(focusedIndex, "auto");
  }, [focusedIndex, focusedMode, scrollToCard]);

  useEffect(() => {
    if (!focusedMode || !stripRef.current) return;
    const el = stripRef.current;
    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const viewportCenter = el.scrollLeft + el.clientWidth / 2;
        const laneOrigin = viewportCenter - sidePad;
        const rawIndex = (laneOrigin - CARD_WIDTH / 2) / (CARD_WIDTH + CARD_GAP);
        const idx = Math.round(rawIndex);
        if (idx !== focusedIndex && idx >= 0 && idx < variants.length) setFocusedIndex(idx);
        ticking = false;
      });
    }
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [focusedMode, variants.length, focusedIndex, sidePad]);

  // Close on ESC
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!open) return;
      if (e.key === "Escape") {
        if (zoomCard) { setZoomCard(null); return; }
        onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, zoomCard, onClose]);

  // Select the card shown in the modal (put this high in the component)
const infoCard = useMemo(() => {
  const byId =
    Array.isArray(variants) && selectedId
      ? (variants as any[]).find((c) => c?.id === selectedId) || null
      : null;
  return byId || base || (preload as any)?.card || null;
}, [variants, selectedId, base, preload]);

// Safe id for side effects (rulings, wishlist load, etc.)
const infoCardId =
  (infoCard as any)?.id ||
  (base as any)?.id ||
  (preload as any)?.card?.id ||
  null;

  // Add-to-collection flow helpers
  const onAdd = useCallback((card: ScryfallCard) => {
    if (!card) return;
    setAddCard(card);
    setAddRegularPrice(card?.prices?.usd ? Number(card.prices.usd) : null);
    setAddFoilPrice(card?.prices?.usd_foil ? Number(card.prices.usd_foil) : null);
  }, []);

  const closeAddFlow = useCallback(() => {
    setAddCard(null);
    setAddRegularPrice(null);
    setAddFoilPrice(null);
  }, []);

  const handleAdded = useCallback(() => {
    try { toast({ description: "Added to collection." }); } catch {}
    try { props.onAdded?.(); } catch {}
    if (props.closeOnAdd) {
      try { props.onClose(); } catch {}
    }
    closeAddFlow();
  }, [closeAddFlow, props]);

  // Wishlist toggle (Firestore-backed with safe fallbacks)
  const toggleFavorite = useCallback(async (card: ScryfallCard) => {
    const user = auth.currentUser;
    if (!user) {
      toast({ description: "Sign in to use wishlist." });
      return;
    }
    if (wishlistDisabled) {
      toast({ description: "Wishlist is temporarily unavailable." });
      return;
    }
    try {
      if (wishlisted) {
        // Remove from users/{uid}/wishlist
        let id = wishlistDocId;
        if (!id) {
          const qref = query(
            collection(db, "users", user.uid, "wishlist"),
            where("scryfall_id", "==", card.id)
          );
          const snap = await getDocs(qref);
          if (!snap.empty) id = snap.docs[0].id;
        }
        if (id) {
          await deleteDoc(doc(db, "users", user.uid, "wishlist", id));
        }
        setWishlisted(false);
        setWishlistDocId(null);
        toast({ description: "Removed from wishlist." });
      } else {
        // Add to users/{uid}/wishlist (restored richer payload)
        const img =
          card.card_faces?.[0]?.image_uris?.normal ||
          card.image_uris?.normal ||
          card.card_faces?.[0]?.image_uris?.large ||
          card.image_uris?.large ||
          null;

        const payload = {
          uid: user.uid,
          scryfall_id: card.id,
          oracle_id: card.oracle_id ?? base?.oracle_id ?? null,
          name: card.name,
          set: card.set ?? null,
          number: card.collector_number ?? null,
          image: img,
          card_faces: card.card_faces ?? base?.card_faces ?? null,
          foil: Array.isArray(card.finishes) ? card.finishes.includes("foil") : false,
          condition: "NM",
          desired_qty: 1,
          createdAt: serverTimestamp(),
        };
        const ref = await addDoc(collection(db, "users", user.uid, "wishlist"), payload as any);
        setWishlistDocId(ref.id);
        setWishlisted(true);
        toast({ description: `Added “${card.name}” to wishlist ✓` });
      }
    } catch (e: any) {
      console.error(e);
      if (String(e?.code) === "permission-denied" || /insufficient permissions/i.test(String(e?.message))) {
        setWishlistDisabled(true);
        toast({ description: "Wishlist is disabled by permissions." });
      } else {
        toast({ description: e?.message || "Wishlist action failed." });
      }
    }
  }, [wishlisted, wishlistDocId, wishlistDisabled, base?.oracle_id]);

  // ADD: load initial wishlisted state when a card is shown
  useEffect(() => {
    (async () => {
      if (!open) return;
      const user = auth.currentUser;
      const id = infoCard?.id || base?.id;
      if (!user || !id) { setWishlisted(false); setWishlistDocId(null); return; }
      try {
        const snap = await getDocs(query(
          collection(db, "users", user.uid, "wishlist"),
          where("scryfall_id", "==", id)
        ));
        if (!snap.empty) {
          setWishlisted(true);
          setWishlistDocId(snap.docs[0].id);
        } else {
          setWishlisted(false);
          setWishlistDocId(null);
        }
      } catch {
        setWishlisted(false);
        setWishlistDocId(null);
      }
    })();
  }, [open, infoCard?.id, base?.id]);

  // Fetch rulings if missing
useEffect(() => {
  if (!open) return;
  const id = infoCardId;
  const oid = (infoCard as any)?.oracle_id || (base as any)?.oracle_id || (preload as any)?.card?.oracle_id || null;
  if (!id && !oid) return;

  const ac = new AbortController();
  // clear previous state while loading
  setRulings(null);

  (async () => {
    try {
      const arr = await fetchRulingsResilient({ id, oracle_id: oid }, ac.signal);
      setRulings(arr);
    } catch {
      setRulings([]); // show "No rulings found."
    }
  })();

  return () => ac.abort();
}, [open, infoCardId, (infoCard as any)?.oracle_id, (base as any)?.oracle_id, (preload as any)?.card?.oracle_id]);

  // Load initial wishlist flag for the shown card
  useEffect(() => {
    (async () => {
      if (!open || !infoCardId) { setWishlisted(false); setWishlistDocId(null); return; }
      const u = auth.currentUser;
      if (!u) { setWishlisted(false); setWishlistDocId(null); return; }
      try {
        const snap = await getDocs(
          query(collection(db, "users", u.uid, "wishlist"), where("scryfall_id", "==", infoCardId))
        );
        if (!snap.empty) {
          setWishlisted(true);
          setWishlistDocId(snap.docs[0].id);
        } else {
          setWishlisted(false);
          setWishlistDocId(null);
        }
      } catch {}
    })();
  }, [open, infoCardId]);

  // --- Quick sanity log when variants populate
  useEffect(() => {
    if (!open) return;
    if (variants.length) {
      console.log(
        "[V2Modal] variants ready:",
        variants.length,
        variants.slice(0, 3).map(v => `${setCodeOf(v)}#${v.collector_number ?? ""}`)
      );
    }
  }, [open, variants.length]);

  // Highlight names for grid glow
  const highlightNames = useHighlightCollectionNames();
  const highlightSet = useMemo<Set<string>>(() => {
    const n: any = highlightNames;
    if (n && typeof n.has === "function") return n as Set<string>;
    const arr: string[] = Array.isArray(n) ? n : [];
    return new Set(arr.map(s => s.toLowerCase()));
  }, [highlightNames]);

  // Combos UI (safe defaults to prevent reference errors if data layer isn’t wired)
  const [showSpellbookEmbed, setShowSpellbookEmbed] = useState(false);
  const [selectedComboCard, setSelectedComboCard] = useState<string | null>(null);
  const combosLoading = false;
  const combosError: string | null = null;
  const normalizedCombos: any[] = [];
  const comboCardList: { name: string; count: number }[] = [];
  const combosByCard = useMemo(() => new Map<string, any[]>(), []);
  const embedImages: Record<string, string> = {};

  // --- Consume preload immediately on open
  useEffect(() => {
    if (!open) return;

    hydratedFromPreloadRef.current = false;

    if (preload?.card) {
      // Prefer caller-supplied variants, then prints
      const rawList = (Array.isArray(preload.variants) && preload.variants.length
        ? preload.variants
        : (Array.isArray(preload.prints) ? preload.prints : [])) as ScryfallCard[];

      // Only trust caller "variants" if they are actual prints for the same oracle_id
      const targetOid = preload.card?.oracle_id ?? null;
      const sameOracle = !!(targetOid && rawList.length > 1 && rawList.every(v => v?.oracle_id === targetOid));

      const useList = sameOracle ? rawList : [];
      const ordered = uniqById(sortPrintsNewestFirst(useList.length ? useList : [preload.card]));

      setBase(preload.card);
      setVariants(ordered);
      setSelectedId(preload.card.id || ordered[0]?.id || null);
      setRulings(Array.isArray(preload.rulings) ? preload.rulings : null);
      setErr(null);
      setLoading(false);

      // Only skip backend fetch when caller provided true prints (same oracle) and >1 items
      if (sameOracle && useList.length > 1) {
        hydratedFromPreloadRef.current = true;
      } else if (preload.card) {
        // Enrich immediately from backend if 0/1 or cross-card list provided
        const ac = new AbortController();
        let aborted = false;
        (async () => {
          try {
            const enriched = await enrichVariantsFromBackend(preload.card!, ac.signal);
            if (!aborted && enriched.length > ordered.length) setVariants(enriched);
          } catch {}
        })();
        return () => { aborted = true; ac.abort(); };
      }
    }
  }, [open, preload?.card, preload?.prints, preload?.variants, preload?.rulings]);

  // Optional: reset the flag when modal closes
  useEffect(() => {
    if (!open) hydratedFromPreloadRef.current = false;
  }, [open]);

  // --- Backend bootstrap; skip only if preload supplied multiple variants
  useEffect(() => {
    if (!open) return;
    if (hydratedFromPreloadRef.current) return;

    // Build robust keys (fallback to preload/base)
    const refId = baseCard?.scryfall_id || (baseCard as any)?.id || preload?.card?.id || base?.id || null;
    const refOid = baseCard?.oracle_id || preload?.card?.oracle_id || base?.oracle_id || null;
    const refName = baseCard?.name || preload?.card?.name || base?.name || null;
    const hasKeys = !!(refId || refOid || refName);
    if (!hasKeys) {
      setBase(null);
      setVariants([]);
      setErr(null);
      return;
    }

    const ac = new AbortController();
    let aborted = false;
    setLoading(true);
    setErr(null);

    (async () => {
      try {
        const { card, prints, rulings } = await fetchBundleFromBackend(
          { scryfall_id: refId, oracle_id: refOid, name: refName },
          ac.signal
        );
        const resolved =
          card ??
          preload?.card ??
          base ??
          (Array.isArray(prints) && prints.length ? prints[0] : null);
         if (!resolved?.id) throw new Error("Unable to resolve card from backend");

         setBase(prev => prev ?? resolved);

         // Seed variants from bundle/resolved
         const seed = uniqById(
          (Array.isArray(prints) && prints.length ? prints : resolved ? [resolved] : []) as ScryfallCard[]
        );
         setVariants(prev => (prev.length ? prev : seed));
         setSelectedId(prev => prev || resolved.id);
         setRulings(rulings ?? null);
         setErr(null);

        // Enrich if we still have 0/1
        if (!aborted && seed.length <= 1) {
           const enriched = await enrichVariantsFromBackend(resolved, ac.signal);
          if (!aborted && enriched.length > seed.length) {
            setVariants(enriched);
            if (debugOn) console.log("[V2Modal] variants enriched via backend", { seed: seed.length, final: enriched.length });
          }
        }
      } catch (e: any) {
       if (!aborted) {
         // Graceful fallback: try enriching from whatever we already have
         const fallback = preload?.card || base || null;
         if (fallback?.id) {
           try {
             const enriched = await enrichVariantsFromBackend(fallback, ac.signal);
             setBase(prev => prev ?? fallback);
             setVariants(enriched.length ? enriched : [fallback]);
             setSelectedId(prev => prev || fallback.id);
             setErr(null);
           } catch {
             setErr(e?.message || "Could not load card");
           }
         } else {
           setErr(e?.message || "Could not load card");
         }
       }
      } finally {
        if (!aborted) setLoading(false);
      }
    })();

    return () => { aborted = true; ac.abort(); };
  }, [
    open,
    baseCard?.scryfall_id,
    (baseCard as any)?.id,
    baseCard?.oracle_id,
    baseCard?.name,
    preload?.variants,
    preload?.prints,
    preload?.card,
  ]);

  // Keep selection stable when variants/base change
  useEffect(() => {
    setSelectedId(prev => {
      if (prev && variants.some(v => v.id === prev)) return prev;
      if (base?.id && variants.some(v => v.id === base.id)) return base.id;
      return variants[0]?.id || base?.id || null;
    });
  }, [variants, base?.id]);

  // The card currently shown in Grid mode (image + details)
  const detailsCard = infoCard;

  const focusedCard = useMemo(
    () => (Array.isArray(variants) ? (variants[focusedIndex] ?? variants[0] ?? null) : null) || base || null,
    [variants, focusedIndex, base]
  );

  // Face-aware image helper and flip action
  const getFaceIndex = useCallback((c: ScryfallCard | null) => {
    if (!c || !c.id) return 0;
    const idx = faceIndexById[c.id] ?? 0;
    const max = Array.isArray(c?.card_faces) ? Math.max(0, c.card_faces.length - 1) : 0;
    return Math.min(idx, max);
  }, [faceIndexById]);

  function getCardImageUrlFace(card: any, size: "small" | "normal" | "large" = "large"): string {
    if (!card) return "/images/sg-card-back.png";
    const idx = getFaceIndex(card);
    const faceUris = card.card_faces?.[idx]?.image_uris || card.card_faces?.[0]?.image_uris || null;
    const obj =
      (size === "large" ? (faceUris?.large || card.image_uris?.large) : null) ||
      (size === "normal" ? (faceUris?.normal || card.image_uris?.normal) : null) ||
      (size === "small" ? (faceUris?.small || card.image_uris?.small) : null) ||
      faceUris?.large || card.image_uris?.large || faceUris?.normal || card.image_uris?.normal || faceUris?.small || card.image_uris?.small || null;
    if (obj) return obj;
    return card?.id ? scryImageUrl(card.id, size) : "/images/sg-card-back.png";
  }

  const flipCardFace = useCallback((c: ScryfallCard | null) => {
    if (!c || !Array.isArray(c.card_faces) || c.card_faces.length < 2) return;
    setFaceIndexById(prev => {
      const cur = prev[c.id] ?? 0;
      const next = (cur + 1) % c.card_faces.length;
      return { ...prev, [c.id]: next };
    });
  }, []);

  // Detail overlay helpers used by enrich effect
  const DETAIL_FIELDS = ["oracle_text", "type_line", "artist", "legalities", "set_name", "color_identity", "card_faces"] as const;
  const hasFaceOracleText = (c?: ScryfallCard | null) =>
    Array.isArray(c?.card_faces) && c!.card_faces!.some((f: any) => !!f?.oracle_text);
  function overlayDetails(dst: ScryfallCard, rich: ScryfallCard): ScryfallCard {
    const patch: Record<string, any> = {};
    for (const k of DETAIL_FIELDS) {
      const cur = (dst as any)[k];
      const v = (rich as any)[k];
      if (k === "card_faces") {
        const dstFacesMissing = !hasFaceOracleText(dst);
        const richHas = hasFaceOracleText(rich);
        if (dstFacesMissing && richHas) patch[k] = Array.isArray(v) ? [...v] : v;
        continue;
      }
      if (cur == null && v != null) patch[k] = v;
    }
    return Object.keys(patch).length ? { ...dst, ...patch } : dst;
  }

  // Enrich the currently displayed card when selection changes (reuses your api + overlayDetails)
  useEffect(() => {
    if (!open || !infoCard?.id) return;
    const ac = new AbortController();
    (async () => {
      try {
        const rich = await api.getCardById(infoCard.id, ac.signal);
        if (!rich) return;
        setVariants((prev: any[]) => prev.map((v) => (v.id === infoCard.id ? overlayDetails(v, rich) : v)));
        if (base?.id === infoCard.id) {
          setBase((prev: any) => (prev ? overlayDetails(prev, rich) : rich));
        }
      } catch {}
    })();
    return () => ac.abort();
  }, [open, infoCard?.id, base?.id]);

  // --- Details: read from the currently displayed card (with overlay if you keep one) ---
  const oracleText = useMemo(() => {
    if (!detailsCard) return "";
    if (Array.isArray(detailsCard.card_faces) && detailsCard.card_faces.some((f: any) => !!f?.oracle_text)) {
      return detailsCard.card_faces.map((f: any) => f?.oracle_text).filter(Boolean).join("\n//\n");
    }
    return (detailsCard as any).oracle_text || "";
  }, [detailsCard]);
  const typeLine = (detailsCard as any)?.type_line || "";
  const artist = (detailsCard as any)?.artist || null;
  // Rulings status (simple wiring; real fetch already populates `rulings`)
  const rulingsLoading = false;
  const rulingsError: string | null = null;
  const hasRulings = Array.isArray(rulings) && rulings.length > 0;
  const legalitiesList = useMemo(() => {
    const leg = (detailsCard as any)?.legalities as Record<string, string> | undefined;
    if (!leg) return [];
    return Object.entries(leg)
      .filter(([, v]) => v === "legal" || v === "restricted" || v === "banned")
      .map(([format, status]) => ({ format, status }));
  }, [detailsCard]);

// Reprint filters (by set label)
const setLabel = useCallback((c: ScryfallCard | null) => {
    if (!c) return "";
    return (c.set_name && String(c.set_name)) || (c.set && String(c.set).toUpperCase()) || "";
  }, []);
  const [reprintFilters, setReprintFilters] = useState<Set<string>>(new Set());
  const reprintSets = useMemo(() => {
    const labels = new Set<string>();
    for (const v of variants) {
      const s = setLabel(v);
      if (s) labels.add(s);
    }
    // Hide the currently displayed card’s set in the “Reprinted In” list
    const current = setLabel(detailsCard);
    if (current) labels.delete(current);
    return Array.from(labels).sort((a, b) => a.localeCompare(b));
  }, [variants, detailsCard, setLabel]);
  const toggleReprintFilter = useCallback((label: string) => {
    setReprintFilters(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);
  const clearReprintFilters = useCallback(() => setReprintFilters(new Set()), []);
  const filteredVariants = useMemo(() => {
    if (reprintFilters.size === 0) return variants;
    return variants.filter(v => reprintFilters.has(setLabel(v)));
  }, [variants, reprintFilters, setLabel]);

  // ============================= RENDER =============================
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      {focusedMode ? (
        <div
          className="fixed inset-0 z-[120]"
          style={{ background: "transparent" }}
          onClick={() => {
            if (zoomCard) { setZoomCard(null); return; }
            onClose();
          }}
          aria-hidden
        />
      ) : (
        <div
          className="fixed inset-0 z-[120] bg-black/70"
          onClick={() => {
            if (zoomCard) { setZoomCard(null); return; }
            onClose();
          }}
          aria-hidden
        />
      )}

      <div
        className="fixed inset-0 z-[121] p-0 flex flex-col items-center overflow-hidden bg-transparent"
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        <div
          className={`w-full h-[100dvh] flex flex-col overflow-hidden ${
            focusedMode
              ? "bg-transparent border-0 shadow-none backdrop-blur-none"
              : "rounded-none sm:rounded-2xl border border-white/10 bg-gray-950/95 backdrop-blur shadow-2xl"
          }`}
          style={focusedMode ? { background: "transparent" } : undefined}
        >
          {/* Header (grid mode) */}
          {!focusedMode && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={toggleMode}
                  className="px-2 py-1 rounded-md text-[11px] font-medium border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 shrink-0"
                  title="Switch to Focus Mode"
                >
                  Focus Mode
                </button>
                <div className="font-semibold text-white text-base sm:text-lg truncate">
                  {focusedCard?.name || base?.name || "Loading…"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {[
                  { key: "details", label: "Details" },
                  { key: "rulings", label: "Notes & Rulings" },
                ].map(t => {
                  const active = rightTab === (t.key as any);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setRightTab(t.key as "details" | "rulings")}
                      aria-pressed={active}
                      className={`px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide border transition ${
                        active
                          ? "bg-emerald-600/30 border-emerald-400/40 text-emerald-200 shadow-inner"
                          : "bg-white/5 border-white/15 text-white/60 hover:bg-white/10 hover:text-white/80"
                      }`}
                      title={t.label}
                    >
                      {t.label}
                    </button>
                  );
                })}
                <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close" className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          <div className={`flex-1 overflow-auto`} style={focusedMode ? { background: "transparent" } : undefined}>
            {focusedMode ? (
              // Focus Mode filmstrip
              <div className="relative h-[100dvh] w-full overflow-hidden">
                {(loading || err || variants.length === 0) && (
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="rounded-lg border border-white/10 bg-black/60 px-4 py-3 text-center max-w-md">
                      <div className="text-sm text-white/80 font-medium">
                        {loading ? "Loading card…" : err ? "Could not load this card." : "No variants found."}
                      </div>
                      {(base || baseCard?.scryfall_id) && (
                        <div className="mt-3 mx-auto w-[min(68vw,360px)] aspect-[3/4] rounded-md overflow-hidden ring-1 ring-white/15 bg-black">
                          <img
                            src={getCardImageUrl(base || { id: baseCard!.scryfall_id! } as any)}
                            alt={base?.name || baseCard?.name || "Card"}
                            className="w-full h-full object-contain select-none pointer-events-none"
                            draggable={false}
                          />
                        </div>
                      )}
                      {!!err && <div className="mt-2 text-xs text-white/60">{err}</div>}
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 flex items-center">
                  <div
                    ref={stripRef}
                    className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory gap-8 w-full h-auto px-0"
                    style={{
                      scrollSnapType: "x mandatory",
                      WebkitOverflowScrolling: "touch",
                      paddingLeft: sidePad,
                      paddingRight: sidePad,
                      background: "transparent",
                    }}
                  >
                    {variants.map((v, i) => {
                      const active = i === focusedIndex;
                      const scale = active ? 1 : 0.82;
                      const elevate = active ? "shadow-2xl ring-1 ring-white/25" : "ring-1 ring-white/10";
                      return (
                        <div
                          key={v.id}
                          data-variant-card={i}
                          data-current-focused-card={active ? "true" : undefined}
                          className="relative snap-center flex-shrink-0"
                          style={{ width: CARD_WIDTH, transition: "transform 260ms ease, filter 260ms ease", transform: `translateY(${active ? 0 : 22}px) scale(${scale})` }}
                          aria-current={active}
                        >
                          <div className={`relative rounded-xl overflow-hidden bg-black/70 ${elevate}`}>
                            <img src={getCardImageUrlFace(v)} alt={v.name} className="w-full h-full object-contain select-none pointer-events-none" draggable={false} style={{ aspectRatio: "3 / 4" }} />
                            {Array.isArray(v.card_faces) && v.card_faces.length >= 2 && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); flipCardFace(v); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8 rounded-full bg-black/70 border border-white/25 text-white/90 hover:bg-black/60 z-10"
                                aria-label="Flip card face"
                                title="Flip card face"
                              >
                                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="opacity-90">
                                  <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8c2.76 0 5 2.24 5 5 0 .65-.13 1.27-.36 1.83l1.53 1.53A7.96 7.96 0 0 0 20 13c0-2.21-.9-4.21-2.35-5.65zM6.35 17.65A7.95 7.95 0 0 0 12 20v3l5-5-5-5v3c-2.76 0-5-2.24-5-5 0-.65.13-1.27.36-1.83L5.83 7.59A7.96 7.96 0 0 0 4 11c0 2.21.9 4.21 2.35 5.65z"/>
                                </svg>
                              </button>
                            )}
                            {!active && <div className="absolute inset-0 bg-gray-900/25 backdrop-blur-sm" />}
                            {/* Removed in-card pricing to allow full-image view in Focus Mode */}
                            <button type="button" onClick={() => setFocusedIndex(i)} className="absolute inset-0 z-0" aria-label={`Focus ${v.name}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {variants.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={prevVariant}
                      disabled={focusedIndex === 0}
                      aria-label="Previous variant"
                      className={`hidden md:flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full z-30 bg-black/50 hover:bg-black/60 border border-white/25 text-white transition ${
                        focusedIndex === 0 ? "opacity-25 cursor-default hover:bg-black/50" : ""
                      }`}
                    >
                      <ChevronLeft className="w-7 h-7" />
                    </button>
                    <button
                      type="button"
                      onClick={nextVariant}
                      disabled={focusedIndex === variants.length - 1}
                      aria-label="Next variant"
                      className={`hidden md:flex items-center justify-center absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full z-30 bg-black/50 hover:bg-black/60 border border-white/25 text-white transition ${
                        focusedIndex === variants.length - 1 ? "opacity-25 cursor-default hover:bg-black/50" : ""
                      }`}
                    >
                      <ChevronRight className="w-7 h-7" />
                    </button>
                  </>
                )}

                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 w-full max-w-xl flex flex-wrap gap-3 justify-center px-1">
                  {/* Pricing shown directly below focused card image, above buttons */}
                  <div className="w-full flex items-center justify-center mb-2">
                    {(focusedCard as any)?.id && (
                      <PriceOverlay scryfallId={(focusedCard as any).id} compact size="sm" tone="emerald" />
                    )}
                  </div>
                  <Button className="flex-1 min-w-[130px] bg-emerald-600 hover:bg-emerald-500 text-white font-semibold" onClick={() => onAdd(focusedCard || base!)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  <Button disabled={wishlistDisabled} className="flex-1 min-w-[130px] bg-pink-600 hover:bg-pink-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => toggleFavorite(focusedCard || base!)}>
                    <Heart className="w-4 h-4 mr-1" />
                    {wishlisted ? "Wishlisted" : "Wishlist"}
                  </Button>
                  <Button className="flex-1 min-w-[130px] bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold" onClick={() => handleCopyImage(focusedCard || base!)}>
                    <Share2 className="w-4 h-4 mr-1" />
                    Copy Img
                  </Button>
                  <Button className="flex-1 min-w-[130px] bg-indigo-600 hover:bg-indigo-500 text-white font-semibold" onClick={toggleMode}>
                    Grid Mode
                  </Button>
                  <Button className="min-w-[90px] bg-rose-600 hover:bg-rose-500 text-white font-semibold" onClick={onClose} aria-label="Close">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              // Grid Mode
              <div className="min-h-[100dvh] w-full px-4 pt-10 pb-10 space-y-10">
                            {/* Top two-column */}
                <div className="w-full flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex-shrink-0 w-full md:w-auto md:max-w-[24rem] mx-auto md:mx-0">
                    <div className="w-full aspect-[3/4] bg-black/80 rounded-lg overflow-hidden ring-1 ring-white/10 relative">
                      <img src={getCardImageUrlFace(infoCard)} alt={infoCard?.name || "Card"} className="w-full h-full object-contain" />
                      {Array.isArray((infoCard as any)?.card_faces) && (infoCard as any).card_faces.length >= 2 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); flipCardFace(infoCard); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8 rounded-full bg-black/70 border border-white/25 text-white/90 hover:bg-black/60"
                          aria-label="Flip card face"
                          title="Flip card face"
                        >
                          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="opacity-90">
                            <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8c2.76 0 5 2.24 5 5 0 .65-.13 1.27-.36 1.83l1.53 1.53A7.96 7.96 0 0 0 20 13c0-2.21-.9-4.21-2.35-5.65zM6.35 17.65A7.95 7.95 0 0 0 12 20v3l5-5-5-5v3c-2.76 0-5-2.24-5-5 0-.65.13-1.27.36-1.83L5.83 7.59A7.96 7.96 0 0 0 4 11c0 2.21.9 4.21 2.35 5.65z"/>
                          </svg>
                        </button>
                      )}
                    </div>
 <div className="mt-3 flex flex-wrap gap-2">
   <Button className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold" onClick={() => onAdd(infoCard || base!)}>
     <Plus className="w-4 h-4 mr-1" /> Add
   </Button>
   <Button disabled={wishlistDisabled} variant="outline" className="border-pink-400/50 text-pink-200 hover:bg-pink-500/10 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => toggleFavorite(infoCard || base!)}>
     <Heart className="w-4 h-4 mr-1" /> {wishlisted ? "Wishlisted" : "Wishlist"}
   </Button>
   <Button variant="outline" className="border-amber-400/50 text-amber-200 hover:bg-amber-500/10" onClick={() => handleCopyImage(infoCard || base!)}>
     <Share2 className="w-4 h-4 mr-1" /> Copy Img
   </Button>
 </div>
                  </div>

                  <div className="flex-1 w-full space-y-5">
                    {rightTab === "details" && (
                      <div className="space-y-5">
                        <div className="border-b border-white/10 pb-3">
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-start">
                            <div>
                              <h2 className="text-xl font-semibold text-white mb-1 truncate">{infoCard?.name || base?.name || "Loading…"}</h2>
                              {typeLine && <div className="text-sm text-slate-300">{typeLine}</div>}
                            </div>
                            {infoCard?.id && (
                              <div className="sm:text-right">
                                <PriceOverlay scryfallId={infoCard.id} compact size="sm" tone="cyan" />
                              </div>
                            )}
                          </div>
                        </div>

                        {oracleText && (
                          <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans leading-relaxed max-h-[38vh] overflow-auto pr-2">
                            {oracleText}
                          </pre>
                        )}

                        {(artist || legalitiesList.length || reprintSets.length) && (
                          <div className="space-y-4 text-sm">
                            {artist && (
                              <div>
                                <div className="text-xs uppercase tracking-wide text-white/50 mb-1">Illustrator</div>
                                <div className="text-white/80">{artist}</div>
                              </div>
                            )}
                            {legalitiesList.length > 0 && (
                              <div>
                                <div className="text-xs uppercase tracking-wide text-white/50 mb-1">Legalities</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {legalitiesList.map(l => {
                                    const color =
                                      l.status === "banned"
                                        ? "bg-rose-600/20 text-rose-300 border-rose-400/30"
                                        : l.status === "restricted"
                                        ? "bg-amber-600/20 text-amber-300 border-amber-400/30"
                                        : "bg-emerald-600/20 text-emerald-300 border-emerald-400/30";
                                    return (
                                      <span key={l.format} className={`px-2 py-[3px] rounded-md border text-[11px] font-medium leading-none ${color}`} title={`${l.format}: ${l.status}`}>
                                        {l.format}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {reprintSets.length > 0 && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="text-xs uppercase tracking-wide text-white/50">Reprinted In</div>
                                  {reprintFilters.size > 0 && (
                                    <button
                                      type="button"
                                      onClick={clearReprintFilters}
                                      className="text-[10px] px-2 py-[2px] rounded-md bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {reprintSets.map(s => {
                                    const active = reprintFilters.has(s);
                                    return (
                                      <button
                                        key={s}
                                        type="button"
                                        aria-pressed={active}
                                        onClick={() => toggleReprintFilter(s)}
                                        className={`px-2 py-[4px] rounded-md border text-[11px] font-medium leading-none transition ${
                                          active
                                            ? "bg-emerald-600/25 border-emerald-400/40 text-emerald-200 shadow-inner"
                                            : "border-white/10 bg-white/5 hover:bg-white/10 text-white/70"
                                        }`}
                                        title={active ? `Filter ON: ${s}` : `Click to filter ${s}`}
                                      >
                                        {s}
                                      </button>
                                    );
                                  })}
                                </div>
                                {reprintFilters.size > 0 && (
                                  <div className="mt-2 text-[11px] text-white/50">
                                    Showing {filteredVariants.length} of {variants.length} variants
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {rightTab === "rulings" && (
                      <div className="space-y-6 text-sm">
                        {/* Notes & Rulings */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h2 className="text-base font-semibold text-white">Notes & Rulings (Scryfall)</h2>
                            {rulingsLoading && <span className="text-xs text-white/60">Loading…</span>}
                          </div>
                          {rulingsError && <div className="text-amber-300">{rulingsError}</div>}
                          {!rulingsLoading && !rulingsError && !hasRulings && <div className="text-white/60">No rulings found.</div>}
                          {hasRulings && (
                            <ol className="space-y-3 max-h-[40vh] overflow-auto pr-2">
                              {rulings!.map((r, i) => (
                                <li key={i} className="p-3 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 transition">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[11px] uppercase tracking-wide text-white/50">{r.source}</span>
                                    <span className="text-[11px] text-white/40 tabular-nums">{r.published_at}</span>
                                  </div>
                                  <div className="whitespace-pre-wrap leading-relaxed text-white/85">{r.comment}</div>
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>

                        {/* Release Notes */}
                        <div className="pt-2 border-t border-white/10">
                          <h3 className="text-sm font-semibold text-white mb-2">Release Notes & Rules</h3>
                          <div className="flex flex-col gap-2">
                            <a
                              href={buildReleaseNotesSearch(base?.set_name ?? null, base?.name ?? null)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
                            >
                              Search Wizards Release Notes for {(base?.set_name || base?.set || "this set")}
                            </a>
                            <a
                              href={buildComprehensiveRulesSearch(base?.name ?? null)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
                            >
                              Search the Magic Comprehensive Rules for “{base?.name}”
                            </a>
                          </div>
                        </div>

                        {/* Community / Combos */}
                        <div className="pt-2 border-t border-white/10">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-sm font-semibold text-white">Community / Combos</h3>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setShowSpellbookEmbed(s => !s)}
                                className="text-[11px] px-2 py-[2px] rounded-md bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                                title="Toggle official embedded results"
                              >
                                {showSpellbookEmbed ? "Hide Official Embed" : "Show Official Embed"}
                              </button>

                              {selectedComboCard && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedComboCard(null)}
                                  className="text-[11px] px-2 py-[2px] rounded-md bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                                  title="Clear card filter"
                                >
                                  Clear
                                </button>
                              )}
                              {base?.name && (
                                <a
                                  href={commanderSpellbookSearchUrl(base.name)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[11px] text-white/60 hover:text-white/80 underline underline-offset-2"
                                >
                                                                   Open on Commander Spellbook →
                                </a>
                                                           )}
                            </div>
                          </div>

                          {/* OFFICIAL EMBED */}
                          {showSpellbookEmbed && base?.name && (
                           
                            <div className="mt-3 rounded-lg overflow-hidden border border-white/10 bg-white">
                              <iframe

                                src={spellbookEmbedSrc(base.name)}
                                className="w-full h-[72vh] block"
                                loading="lazy"
                                title="Commander Spellbook results"
                                referrerPolicy="no-referrer"
                              />
                            </div>
                          )}

                          {/* Inline chips + images (API-only) */}
                          {!showSpellbookEmbed && (
                            <>
                              {combosLoading && <div className="text-white/60 mt-2">Loading combos…</div>}
                              {combosError && <div className="mt-2 text-amber-300">{combosError}</div>}

                              {!combosLoading && !combosError && normalizedCombos.length > 0 && (
                                <div className="mt-3">
                                  <div className="text-xs uppercase tracking-wide text-white/50 mb-1">Cards in these combos</div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {comboCardList.slice(0, 60).map(({ name, count }) => {
                                      const active = selectedComboCard === name;
                                      return (
                                        <button
                                          key={name}
                                          type="button"
                                          aria-pressed={active}
                                          onClick={() => setSelectedComboCard(active ? null : name)}
                                          className={`px-2 py-[4px] rounded-md border text-[11px] leading-none transition ${
                                            active
                                              ? "bg-emerald-600/25 border-emerald-400/40 text-emerald-200 shadow-inner"
                                              : "border-white/10 bg-white/5 hover:bg-white/10 text-white/70"
                                          }`}
                                          title={active ? `Hide combos with ${name}` : `Show combos with ${name}`}
                                        >
                                          {name} <span className="opacity-70">({count})</span>
                                        </button>
                                      );
                                    })}
                                    {comboCardList.length > 60 && (
                                      <div className="text-[11px] text-white/40 self-center px-1">Showing top 60 cards by frequency.</div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Groups by selected card */}
                              {!combosLoading && !combosError && normalizedCombos.length > 0 && selectedComboCard && (
                                <>
                                  <div className="mt-3 text-[11px] text-white/50">
                                    Showing{" "}
                                    <span className="tabular-nums">
                                      {(combosByCard.get(selectedComboCard) || []).length}
                                    </span>{" "}
                                    combo{(combosByCard.get(selectedComboCard) || []).length === 1 ? "" : "s"} for{" "}
                                    <span className="font-medium text-white/80">“{selectedComboCard}”</span>.
                                  </div>

                                  <ul className="mt-3 space-y-4 max-h-[45vh] overflow-auto pr-2">
                                    {(combosByCard.get(selectedComboCard) || []).map((c, idx) => {
                                      const link = c.permalink || c.link;
                                      const others = (c.cards || []).filter(n => n !== selectedComboCard);
                                      const img = link ? embedImages[link] : undefined;
                                      return (
                                        <li key={idx} className="p-3 rounded-md border border-white/10 bg-white/5">
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="text-white font-medium">
                                              <span className="text-emerald-300">{selectedComboCard}</span>
                                              {others.length ? <span className="text-white/60"> + </span> : null}
                                              {others.map((n, i) => (
                                                <React.Fragment key={i}>
                                                  <button
                                                    type="button"
                                                    className="underline underline-offset-2 decoration-transparent hover:decoration-current text-white"
                                                    onClick={() => setSelectedComboCard(n)}
                                                    title={`Show combos with ${n}`}
                                                  >
                                                    {n}
                                                  </button>
                                                  {i < others.length - 1 && <span className="text-white/60"> + </span>}
                                                </React.Fragment>
                                              ))}
                                            </div>
                                            {link && (
                                              <a
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[11px] text-white/70 hover:text-white/90 underline underline-offset-2 whitespace-nowrap"
                                              >
                                                View
                                              </a>
                                            )}
                                          </div>

                                          <div className="mt-2">
                                            {img ? (
                                              <a href={link} target="_blank" rel="noopener noreferrer" className="block">
                                                <img
                                                  src={img}
                                                  alt={`Combo preview: ${(c.cards || []).join(" + ")}`}
                                                  className="w-full rounded-md border border-white/10"
                                                  loading="lazy"
                                                />
                                              </a>
                                            ) : link ? (
                                              <div className="text-[12px] text-white/60">Loading preview…</div>
                                            ) : (
                                              <div className="text-[12px] text-white/60">No preview.</div>
                                            )}
                                          </div>

                                          {(Array.isArray(c.results) && c.results.length > 0) && (
                                            <div className="mt-2 text-[12px] text-white/80">
                                              <span className="text-white/70">Result:</span> {c.results.join("; ")}
                                            </div>
                                          )}
                                          {(Array.isArray(c.steps) && c.steps.length > 0) && (
                                            <details className="mt-2 group">
                                              <summary className="cursor-pointer text-[12px] text-white/70 hover:text-white/90 select-none">Steps</summary>
                                              <ol className="mt-1 list-decimal list-inside text-[12px] text-white/80 space-y-0.5">
                                                {c.steps.map((s, i) => (
                                                  <li key={i}>{s}</li>
                                                ))}
                                              </ol>
                                            </details>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>

                                  <div className="mt-3 text-[11px] text-white/40">Source: Spellbook API (official) + OG image per combo.</div>
                                  <div className="mt-1 text-[11px] text-white/40">Data courtesy of Commander Spellbook community.</div>
                                </>
                              )}

                             

                              {/* Empty state */}
                              {!combosLoading && !combosError && normalizedCombos.length === 0 && (
                                <div className="text-white/60 mt-2">
                                  No combos found via the official API for this card.
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Variants */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold tracking-wide text-white/70 uppercase">
                      Variants {filteredVariants.length ? `(${filteredVariants.length}${reprintFilters.size ? ` / ${variants.length}` : ""})` : ""}
                    </h3>
                    {loading && <div className="text-xs text-white/50">Loading…</div>}
                  </div>

                 
                  {err && <div className="text-sm text-amber-300">{err}</div>}
                  {!loading && !err && variants.length === 0 && <div className="text-sm text-white/60">No variants found.</div>}

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                   {filteredVariants.map(v => {
                      const img = getCardImageUrlFace(v, "normal");
                      const glow = v?.name ? highlightSet.has(v.name.toLowerCase()) : false;

                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setSelectedId(v.id)}
                          className={`group relative flex flex-col rounded-lg overflow-hidden border bg-black/40 hover:border-white/40 transition ${
                            glow ? "v2-gold-glow border-amber-300/70" : "border-white/15"
                          }`}
                          title={`${v.name} ${v.set?.toUpperCase?.() || ""} #${v.collector_number || ""}`}
                        >
                          <div className="aspect-[3/4] bg-black/70 overflow-hidden relative">
                            {img ? (
                              <img src={img} alt={v.name} className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform" loading="lazy" />
                            ) : (
                              <div className="grid h-full w-full place-items-center text-[10px] text-white/50">No Image</div>
                            )}
                            {Array.isArray(v.card_faces) && v.card_faces.length >= 2 && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); flipCardFace(v); }}
                                className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/70 border border-white/25 text-white/90 hover:bg-black/60"
                                aria-label="Flip card face"
                                title="Flip card face"
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="opacity-90">
                                  <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8c2.76 0 5 2.24 5 5 0 .65-.13 1.27-.36 1.83l1.53 1.53A7.96 7.96 0 0 0 20 13c0-2.21-.9-4.21-2.35-5.65zM6.35 17.65A7.95 7.95 0 0 0 12 20v3l5-5-5-5v3c-2.76 0-5-2.24-5-5 0-.65.13-1.27.36-1.83L5.83 7.59A7.96 7.96 0 0 0 4 11c0 2.21.9 4.21 2.35 5.65z"/>
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="p-2 text-left space-y-1 text-[11px] leading-tight">
                            <div className="text-[12px] font-medium text-white truncate">{v.set?.toUpperCase()} • #{v.collector_number}</div>
                            <PriceOverlay scryfallId={v.id} compact size="xs" tone="amber" />
                          </div>
                          {glow && <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-amber-400/0 via-amber-400/5 to-amber-500/0" />}
                        </button>
                      );
                  })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Zoom overlay */}
        {zoomCard && (
          <>
            <div className="fixed inset-0 z-[130] bg-black/70" onClick={() => setZoomCard(null)} aria-hidden />
            <div className="fixed inset-0 z-[131] p-4 flex items-start justify-center overflow-auto" onClick={e => e.stopPropagation()}>
             
              <div
                className="relative w-full max-w-[min(520px,92vw)] max-h-[calc(100vh-6rem)] mt-8 rounded-xl border border-white/15 bg-gray-900/95 backdrop-blur shadow-2xl overflow-auto flex flex-col"
                style={{ transform: "scale(0.66)", transformOrigin: "top center" }}
                tabIndex={-1}
              >
                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                  <div className="text-sm font-semibold text-white truncate">{zoomCard.name}</div>
                  <Button variant="ghost" size="sm" onClick={() => setZoomCard(null)} className="text-white/70 hover:text-white">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="px-4 pt-4 pb-3 flex flex-col gap-4 overflow-auto">
                  <div className="w-full aspect-[3/4] rounded-md bg-black overflow-hidden ring-1 ring-white/10">
                    <img src={getCardImageUrl(zoomCard)} alt={zoomCard.name} className="w-full h-full object-contain" draggable={false} />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button className="flex-1 bg-emerald-600/80 hover:bg-emerald-600 text-white font-semibold" onClick={() => onAdd(zoomCard)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                    {inTradesPage && (
                      <>
                        <Button
                          variant="outline"
                          className="flex-1 border-sky-500/50 text-sky-200 hover:bg-sky-500/10"
                          onClick={() => {
                            if (!zoomCard) return;
                            const payload = {
                              origin: "quicksearch",
                              key: `qs-${zoomCard.id}-${Date.now()}`,
                              docId: null,
                              ownerUid: null,
                              scryfall_id: zoomCard.id,
                              name: zoomCard.name,
                              set: zoomCard.set ?? null,
                              number: zoomCard.collector_number ?? null,
                              image: zoomCard.card_faces?.[0]?.image_uris?.normal || zoomCard.image_uris?.normal || null,
                              foil: Boolean(zoomCard.finishes?.includes("foil")),
                              qty: 1,
                              unitPrice: zoomCard.prices?.usd ? Number(zoomCard.prices.usd) : null,
                              meta: { source: "quicksearch" },
                            };
                            try {
                              window.dispatchEvent(new CustomEvent("trade-add-item", { detail: { item: payload, target: { side: "sent" } } }));
                            } catch {}
                            setZoomCard(null);
                            if (inTradesPage) {
                              try { onClose(); } catch {}
                              try { navigate("/trades"); } catch {}
                            }
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add to Send
                        </Button>

                        <Button
                          variant="outline"
                          className="flex-1 border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/10"
                          onClick={() => {
                            if (!zoomCard) return;
                            const payload = {
                              origin: "quicksearch",
                              key: `qs-${zoomCard.id}-${Date.now()}`,
                              docId: null,
                              ownerUid: null,
                              scryfall_id: zoomCard.id,
                              name: zoomCard.name,
                              set: zoomCard.set ?? null,
                              number: zoomCard.collector_number ?? null,
                              image: zoomCard.card_faces?.[0]?.image_uris?.normal || zoomCard.image_uris?.normal || null,
                              foil: Boolean(zoomCard.finishes?.includes("foil")),
                              qty: 1,
                              unitPrice: zoomCard.prices?.usd ? Number(zoomCard.prices.usd) : null,
                              meta: { source: "quicksearch" },
                            };
                            try {
                              window.dispatchEvent(new CustomEvent("trade-add-item", { detail: { item: payload, target: { side: "received" } } }));
                            } catch {}
                            setZoomCard(null);
                            if (inTradesPage) {
                              try { onClose(); } catch {}
                              try { navigate("/trades"); } catch {}
                            }
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add to Receive
                        </Button>
                      </>
                    )}
                    <Button disabled={wishlistDisabled} variant="outline" className="flex-1 border-pink-400/50 text-pink-200 hover:bg-pink-500/10 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => toggleFavorite(zoomCard)}>
                      <Heart className="w-4 h-4 mr-1" />
                      {wishlisted ? "Wishlisted" : "Wishlist"}
                    </Button>
                    <Button variant="outline" className="flex-1 border-amber-400/50 text-amber-200 hover:bg-amber-500/10" onClick={() => handleCopyImage(zoomCard)}>
                      <Share2 className="w-4 h-4 mr-1" />
                      Copy Img
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Add To Collection */}
        {addCard && (
          <div className="fixed inset-0 z-[540] pointer-events-none">
            <div className="pointer-events-auto">
              <AddToCollectionModal
                open
                card={{
                  id: addCard.id,
                  name: addCard.name,
                  oracle_id: addCard.oracle_id,
                  set: addCard.set,
                  collector_number: addCard.collector_number,
                  image_uris: addCard.image_uris,
                  card_faces: addCard.card_faces,
                }}
                onClose={() => {
                  closeAddFlow();
                }}
                onAdded={handleAdded}
                initialPriceRegular={addRegularPrice}
                initialPriceFoil={addFoilPrice}
              />
            </div>
          </div>
        )}
      </div>

      {/* NEW: tiny debug overlay */}
      {debugOn && (
        <div className="fixed bottom-2 left-2 z-[9999] rounded bg-black/80 text-[11px] text-white/80 px-2 py-1 border border-white/20">
          base:{base?.id ? "✓" : "—"} • variants:{variants.length} • rulings:{Array.isArray(rulings) ? rulings.length : "—"}
       
        </div>
      )}

      {/* z-index + glow */}
      <style>{`
        .sonner-toaster,
        [data-radix-toast-viewport],
        .radix-toast-viewport { z-index: 600 !important; }
        .fixed.inset-0.z-[540] * { z-index: inherit; }
        .v2-gold-glow{
          position:relative;
          box-shadow:0 0 0  1px #f5c84b,0 0 6px 1px #f5c84b80,0 0 14px 3px #e1a90060;
          animation:v2GoldPulse 2.4s ease-in-out infinite;
        }
        @keyframes v2GoldPulse {
          0%,100%{box-shadow:0 0 0 1px #f5c84b,0 0 4px 0px #f5c84b66,0 0 12px 2px #e1a90040;}
          50%{box-shadow:0 0 0 1px #ffe38d,0 0 10px 4px #ffd966aa,0 0 22px 8px #f5c84b80;}
        }
      `}</style>
    </>
  );
}

export default V2Modal;